import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, BadgePercent, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFER_SERVICES = [
  "Security Cameras",
  "Access Control",
  "Video Doorbell / Intercom",
  "Structured Cabling",
  "WiFi",
  "Smart Locks",
  "Audio/Visual",
  "TV Mounting",
  "Alarm & Monitoring",
  "Holiday & Permanent Lighting",
  "Other",
];

const CA_PHONE = /^(?:\+?1[\s.-]?)?\(?[2-9]\d{2}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

export const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expiry, setExpiry] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem("sh_offer_seen")) return;
    const t = setTimeout(() => setOpen(true), 13000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem("sh_offer_seen", "1");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!CA_PHONE.test(form.phone.trim())) {
      setError("Please enter a valid Canadian phone number (e.g. 506-555-0123).");
      return;
    }
    if (!form.service) {
      setError("Please select a service you're interested in.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        ...form,
        source_page: window.location.pathname,
      });
      const date = new Date(data.offer_expires_at).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      setExpiry(date);
      sessionStorage.setItem("sh_offer_seen", "1");
    } catch (err) {
      setError("Something went wrong. Please try again or call (506) 871-4144.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            exit={{ y: 40 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative w-full max-w-[22.5rem] bg-white rounded-md shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              data-testid="lead-close-btn"
              aria-label="Close offer"
              className="absolute top-4 right-4 z-10 p-1.5 rounded-md text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="bg-black px-6 pt-6 pb-4 grain relative">
              <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#D4AF37]">
                <BadgePercent className="w-4 h-4" /> Limited Time Offer
              </p>
              <h2 className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tighter text-white leading-tight">
                Get 20% OFF Your First Installation
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-white/75 leading-relaxed">
                Get your free quote today and SAVE 20% on your first qualifying installation. Offer valid for 14 days.
              </p>
            </div>

            {expiry ? (
              <div className="px-8 py-10 text-center" data-testid="lead-success">
                <CheckCircle2 className="w-12 h-12 mx-auto text-[#D4AF37]" />
                <h3 className="mt-4 text-xl font-extrabold tracking-tight text-zinc-900">You're all set!</h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  Your 20% offer is valid for 14 days. A SafeHome team member will contact you shortly.
                </p>
                <p className="mt-4 text-sm font-bold text-zinc-900" data-testid="lead-expiry-date">
                  Offer valid until {expiry}
                </p>
                <button
                  onClick={close}
                  className="mt-6 px-6 py-2.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black text-sm font-semibold transition-all hover:-translate-y-0.5"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 py-5" data-testid="lead-form">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-name">Name</Label>
                    <Input
                      id="lead-name"
                      data-testid="lead-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-phone">Mobile Phone Number</Label>
                    <Input
                      id="lead-phone"
                      data-testid="lead-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="506-555-0123"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Service Interested In</Label>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="lead-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="z-[80]">
                        {OFFER_SERVICES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`lead-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-email">Email <span className="text-zinc-400 font-normal">(optional)</span></Label>
                    <Input
                      id="lead-email"
                      data-testid="lead-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {error && (
                  <p data-testid="lead-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2.5">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="lead-submit-btn"
                  disabled={loading}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-primary bg-brand-primary-hover text-black text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4AF37]/25 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? "Claiming…" : "Claim My 20% OFF"}
                </button>
                <p className="mt-3 text-[11px] text-zinc-400 text-center">
                  Offer applies to qualifying installations. Terms may apply.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
